import { useEffect, useState } from 'react';

import {
  Button as MuiButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Drawer from '@/components/wrapped/Drawer';
import RichTextEditor from '@/components/wrapped/RichTextEditor';

export type CallNoteFields = {
  title: string;
  notes: string;
};

// The editor holds HTML; the call list renders notes as plain text, so flatten
// to readable text on save (line breaks from blocks, tags dropped).
const htmlToText = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|ul|ol)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/**
 * The create-call-note drawer: a right-anchored surface for writing a call note,
 * mirroring the action drawer. Just a title and a rich-text notes body. Owns its
 * own form state; the call list lives in ControlPanel, which does the save.
 */
const LogCallDrawer = ({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (fields: CallNoteFields) => void;
}) => {
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('');
  // Bumped each open to remount (and clear) the uncontrolled editor.
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setHtml('');
    setEditorKey((k) => k + 1);
  }, [open]);

  const notes = htmlToText(html);
  const valid = title.trim().length > 0 && notes.length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title='Create call note'
      footer={
        <Stack direction='row' spacing={3}>
          <MuiButton
            variant='outlined'
            color='primary'
            fullWidth
            onClick={onClose}
          >
            Cancel
          </MuiButton>
          <Button
            variant='primary'
            size='md'
            isFullWidth
            isDisabled={!valid}
            ariaLabel='Save'
            onPress={() => onSubmit({ title: title.trim(), notes })}
          >
            Save
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Title'
          required
          placeholder='e.g. Initial assessment call'
          variant='filled'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />

        <Stack spacing={1}>
          <Typography variant='body2' color='text.secondary'>
            Notes
          </Typography>
          <RichTextEditor
            key={editorKey}
            placeholder='Enter call notes…'
            onChange={setHtml}
          />
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default LogCallDrawer;
